import { TransformedMessage } from '@provision/chat-utils';
import { ConversationAttachment } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import {
  FileAttributeItem,
  getAttachmentsFromEditor,
  RichTextEditorFile,
} from '@provision/rich-text-editor';
import { useCallback } from 'react';
import { UPDATE_CONVERSATION_PRIVATE_MESSAGE_MUTATION } from 'src/graphql/entities/conversation-message/conversation-message.operations';
import { useFileUpload } from 'src/graphql/entities/files-upload';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { ConversationMessageRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';

const mapFileToFileAttributeItem = (
  file: ConversationAttachment & {
    url: string;
  },
): FileAttributeItem & { id?: string } => ({
  id: file.id,
  url: file.url,
  name: file.fileName,
  size: file.size,
  type: file.contentType,
});

export const useConversationPrivateMessageUpdate = () => {
  const { showErrorAlert } = useNotifications();

  const { uploadFile } = useFileUpload();

  const [updateConversationPrivateMessageMutation, { loading }] = useMutation(
    UPDATE_CONVERSATION_PRIVATE_MESSAGE_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong updating conversation message, please try again');
      },
    },
  );

  const updatePrivateMessage = useCallback(
    async (
      newMessage: {
        message: string;
        attachments: RichTextEditorFile[];
        editedAt: string;
      },
      originalMessage: TransformedMessage,
    ) => {
      const editorNewAttachments = newMessage.attachments.filter((newAttachment) => {
        return !('id' in newAttachment);
      }) as File[];

      let uploadedAttachments: FileAttributeItem[] = [];
      if (editorNewAttachments.length) {
        uploadedAttachments = await getAttachmentsFromEditor({
          files: editorNewAttachments,
          attachmentsBucketFolder: `${originalMessage.partnerId}/${originalMessage.conversationId}`,
          uploadFile,
        });
      }

      const updatedAttachments: FileAttributeItem[] = newMessage.attachments
        .map((it) =>
          'url' in it
            ? mapFileToFileAttributeItem(it)
            : uploadedAttachments.find((attachment) => attachment.name === it.name),
        )
        .filter((it): it is FileAttributeItem => !!it);

      const params = {
        newMessage: {
          body: newMessage.message,
          editedAt: newMessage.editedAt,
          attachments: updatedAttachments,
        },
        originalMessage: {
          id: originalMessage.id,
          partnerId: originalMessage.partnerId,
          conversationId: originalMessage.conversationId,
          attachments: originalMessage.attachments.map(mapFileToFileAttributeItem),
        },
      };

      return updateConversationPrivateMessageMutation({
        variables: {
          params,
        },
        optimisticResponse: {
          updateConversationPrivateMessage: [
            {
              __typename: 'ConversationMessage',
              id: originalMessage.id,
              conversationMessageId: originalMessage.id,
              body: newMessage.message,
              editedAt: newMessage.editedAt,
              attachments: updatedAttachments,
              createdAt: originalMessage.createdAt,
              partnerId: originalMessage.partnerId,
              isPrivate: true,
            },
          ],
        },
        update: (
          cache,
          {
            data: response,
          }: {
            data?: {
              updateConversationPrivateMessage: Partial<ConversationMessageRaw[]>;
            };
          },
        ) => {
          const message = response?.updateConversationPrivateMessage?.[0];

          if (!message) {
            return;
          }

          const messageId = message.conversationMessageId;
          // find message in cache by conversationMessageId
          const messageToUpdate = cache.identify({
            __typename: 'ConversationMessage',
            id: messageId,
          });

          const fields = generateFieldModifiersMap({
            body: message.body,
            editedAt: message.editedAt || new Date().toISOString(),
            attachments: message.attachments,
          });

          cache.modify({
            id: messageToUpdate,
            fields,
          });
        },
      });
    },
    [updateConversationPrivateMessageMutation, uploadFile],
  );

  return {
    loading,
    updatePrivateMessage,
  };
};
